import { RootState } from '../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';
import { IPaginationResponse } from '../interfaces/responces/IPagination.response';
import { IStockInventory, IStockInventoryFilter } from '../interfaces/IStockInventory';

export const stockInventoryApi = createApi({
  reducerPath: 'stockInventoryApi',
  tagTypes: ['StockInventory'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      token && headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getStockInventoriesList: build.query<
      IPaginationResponse<IStockInventory>,
      IStockInventoryFilter
    >({
      transformResponse: (response: { data: IPaginationResponse<any> }) =>
        response.data,
      query: (params) => ({
        url: `/stock-inventories/list`,
        method: 'GET',
        params
      }),
      providesTags: (result) => ['StockInventory'],
    }),
    getStockInventoriesListByStockOwner: build.query<
      IPaginationResponse<IStockInventory>,
      Omit<IStockInventoryFilter, 'warehouseId' | 'productExternalId'> & {
        stockOwnerId: number;
        hasPallet?: boolean;
      }
    >({
      transformResponse: (response: { data: IPaginationResponse<any> }) =>
        response.data,
      query: (params) => ({
        url: `/stock-inventories/list-by-stock-owner`,
        method: 'GET',
        params
      }),
      providesTags: (result) => ['StockInventory'],
    }),
    getStockInventoryList: build.query<
      any,
      { page?: number; query?: string; state?: string }
    >({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ page = 1, query = '', state = '' }) => ({
        url: `/stock-inventories`,
        method: 'GET',
        params: { page, query, state },
      }),
    }),
    getStockInventoryById: build.query<any, number>({
      transformResponse: (response: { data: any }) => response.data,
      query: (id) => ({
        url: `/stock-inventories/${id}`,
        method: 'GET',
      }),
    }),
    getStockInventoryByPalletId: build.query<any, number>({
      transformResponse: (response: { data: any }) => response.data,
      query: (palletId) => ({
        url: `/stock-inventories/by-pallet/${palletId}`,
        method: 'GET',
      }),
    }),
    createStockInventory: build.mutation<any, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: `/stock-inventories`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['StockInventory'],
    }),
    updateStockInventory: build.mutation<any, { id: number; data: any }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ id, data }) => ({
        url: `/stock-inventories/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['StockInventory'],
    }),
    deleteStockInventory: build.mutation<any, number>({
      transformResponse: (response: { data: any }) => response.data,
      query: (id) => ({
        url: `/stock-inventories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['StockInventory'],
    }),
    getStockInventoryStats: build.query<any, void>({
      transformResponse: (response: { data: any }) => response.data,
      query: () => ({
        url: `/stock-inventories/stats`,
        method: 'GET',
      }),
    }),
    exportStockInventory: build.mutation<
      any,
      { ids: number[]; format?: string }
    >({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ ids, format = 'xlsx' }) => ({
        url: `/stock-inventories/export`,
        method: 'POST',
        body: { ids, format },
      }),
    }),
  }),
});

export const {
  useGetStockInventoriesListQuery,
  useLazyGetStockInventoriesListQuery,
  useGetStockInventoriesListByStockOwnerQuery,
  useLazyGetStockInventoriesListByStockOwnerQuery,
  useGetStockInventoryListQuery,
  useLazyGetStockInventoryListQuery,
  useGetStockInventoryByIdQuery,
  useLazyGetStockInventoryByIdQuery,
  useGetStockInventoryByPalletIdQuery,
  useLazyGetStockInventoryByPalletIdQuery,
  useCreateStockInventoryMutation,
  useUpdateStockInventoryMutation,
  useDeleteStockInventoryMutation,
  useGetStockInventoryStatsQuery,
  useLazyGetStockInventoryStatsQuery,
  useExportStockInventoryMutation,
} = stockInventoryApi;

